import React, { useEffect, useState, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { format, addDays, addMonths } from 'date-fns';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  Save,
  BookmarkPlus,
  Users,
  DollarSign,
  Percent,
  CheckCircle,
  RefreshCw,
  Copy,
  Zap,
  User,
  Package,
  FileIcon,
  UserCheck,
  X,
  CreditCard,
  Wallet,
  AlertTriangle,
  ChevronDown,
  Check,
  Plus,
} from 'lucide-react';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  FormDescription,
} from 'app/components/ui/form';
import { Input } from 'app/components/ui/input';
import { Button } from 'app/components/ui/button';
import { Textarea } from 'app/components/ui/textarea';
import { Switch } from 'app/components/ui/switch';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from 'app/components/ui/card';
import { Badge } from 'app/components/ui/badge';
import { Alert, AlertDescription } from 'app/components/ui/alert';
import { Separator } from 'app/components/ui/separator';
import { useCouponDiscountsSlice } from '../slice';
import { useToast } from 'app/components/ui/use-toast';
import { useUserSlice } from 'app/pages/Users/slice';
import { useDownloadPackageSlice } from 'app/pages/DownloadPackages/slice';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'app/components/ui/select';
import { usePaymentMethodsSlice } from 'app/pages/PaymentMethods/slice';
import { endpoints } from 'utils/api/endpoints';

const couponSchema = z
  .object({
    code: z
      .string()
      .min(1, 'Coupon code is required')
      .max(50, 'Code must be less than 50 characters')
      .regex(
        /^[A-Z0-9_-]+$/,
        'Code must contain only uppercase letters, numbers, hyphens, and underscores',
      ),
    name: z
      .string()
      .min(1, 'Coupon name is required')
      .max(100, 'Name must be less than 100 characters'),
    description: z.string().optional(),
    type: z.enum(['percentage', 'fixed'], {
      required_error: 'Please select a discount type',
    }),
    value: z
      .number()
      .min(0.01, 'Value must be greater than 0')
      .or(z.undefined()),
    minimum_amount: z.number().min(0).optional().or(z.undefined()),
    maximum_discount: z.number().min(0).optional().or(z.undefined()),
    usage_limit: z.number().min(1).optional(),
    applies_to: z.enum(['all', '1', '2'], {
      required_error: 'Please select what this coupon applies to',
    }),
    package_ids: z.array(z.number()).optional(),
    user_targeting: z.enum(
      ['all_users', 'first_time_users', 'selected_users'],
      {
        required_error: 'Please select user targeting',
      },
    ),
    selected_user_ids: z.array(z.number()).optional(),
    user_redemption_limit: z.enum(['once_per_user', 'multiple_per_user'], {
      required_error: 'Please select redemption limit',
    }),
    bulk_generate: z.boolean().default(false),
    bulk_count: z.number().min(1).max(1000).optional(),
    payment_method_restriction: z.enum(['all', 'selected'], {
      required_error: 'Please select payment method restriction',
    }),
    allowed_payment_methods: z.array(z.string()).optional(),
    is_active: z.boolean().default(true),
    is_public: z.boolean().default(false),
    display_order: z.number().min(0).default(0),
    valid_from: z.string().min(1, 'Valid from date is required'),
    valid_until: z.string().min(1, 'Valid until date is required'),
  })
  .refine(
    data => {
      if (data.type === 'percentage' && data.value && data.value > 100) {
        return false;
      }
      return true;
    },
    {
      message: 'Percentage discount cannot exceed 100%',
      path: ['value'],
    },
  )
  .refine(
    data => {
      if (data.valid_from && data.valid_until) {
        return new Date(data.valid_from) < new Date(data.valid_until);
      }
      return true;
    },
    {
      message: 'Valid until date must be after valid from date',
      path: ['valid_until'],
    },
  )
  .refine(
    data => {
      if (data.value === undefined || data.value === null) {
        return false;
      }
      return true;
    },
    {
      message: 'Discount value is required',
      path: ['value'],
    },
  )
  .refine(
    data => {
      if (
        data.applies_to === '2' &&
        (!data.package_ids || data.package_ids.length === 0)
      ) {
        return false;
      }
      return true;
    },
    {
      message: 'Please select a package when applying to subscription packages',
      path: ['package_ids'],
    },
  )
  .refine(
    data => {
      if (
        data.user_targeting === 'selected_users' &&
        (!data.selected_user_ids || data.selected_user_ids.length === 0)
      ) {
        return false;
      }
      return true;
    },
    {
      message: 'Please select at least one user when targeting selected users',
      path: ['selected_user_ids'],
    },
  )
  .refine(
    data => {
      if (data.bulk_generate && (!data.bulk_count || data.bulk_count < 1)) {
        return false;
      }
      return true;
    },
    {
      message: 'Please specify the number of coupons to generate (1-1000)',
      path: ['bulk_count'],
    },
  )
  .refine(
    data => {
      if (
        data.payment_method_restriction === 'selected' &&
        (!data.allowed_payment_methods ||
          data.allowed_payment_methods.length === 0)
      ) {
        return false;
      }
      return true;
    },
    {
      message: 'Please select at least one payment method',
      path: ['allowed_payment_methods'],
    },
  );

type CouponFormData = z.infer<typeof couponSchema>;

// Utility functions
const generateCouponCode = (
  prefix: string = '',
  length: number = 8,
): string => {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
  let result = prefix.toUpperCase();

  for (let i = 0; i < length; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length));
  }

  return result;
};

const getQuickDatePresets = () => {
  const today = new Date();
  return {
    today: format(today, 'yyyy-MM-dd'),
    tomorrow: format(addDays(today, 1), 'yyyy-MM-dd'),
    nextWeek: format(addDays(today, 7), 'yyyy-MM-dd'),
    nextMonth: format(addMonths(today, 1), 'yyyy-MM-dd'),
    threeMonths: format(addMonths(today, 3), 'yyyy-MM-dd'),
    sixMonths: format(addMonths(today, 6), 'yyyy-MM-dd'),
    oneYear: format(addMonths(today, 12), 'yyyy-MM-dd'),
  };
};

interface UserItem {
  id: number;
  name: string;
  email: string;
}

const LoadingState: React.FC = () => {
  return (
    <div className="flex items-center justify-center min-h-[400px]">
      <div className="flex flex-col items-center gap-4">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        <p className="text-muted-foreground">Loading...</p>
      </div>
    </div>
  );
};

interface ErrorBoundaryProps {
  children: React.ReactNode;
}

const ErrorBoundary: React.FC<ErrorBoundaryProps> = ({ children }) => {
  const [hasError, setHasError] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    const handleError = (error: ErrorEvent) => {
      setHasError(true);
      setError(error.error);
    };

    window.addEventListener('error', handleError);
    return () => window.removeEventListener('error', handleError);
  }, []);

  if (hasError) {
    return (
      <div className="p-6 mx-auto mt-10 bg-red-50 rounded-lg border border-red-200">
        <div className="flex items-center gap-2 text-red-600 mb-4">
          <AlertTriangle className="h-5 w-5" />
          <h2 className="text-[13px] font-semibold">Something went wrong</h2>
        </div>
        <p className="text-[13px] text-red-600 mb-4">
          {error?.message || 'An unexpected error occurred'}
        </p>
        <Button
          variant="outline"
          onClick={() => window.location.reload()}
          className="w-full"
        >
          <RefreshCw className="h-4 w-4 mr-2" />
          Reload Page
        </Button>
      </div>
    );
  }

  return <>{children}</>;
};

const AddEditCoupon: React.FC = () => {
  // const location = useLocation();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEdit = Boolean(id);

  const {
    useCreateCouponMutation,
    useUpdateCouponMutation,
    useGetCouponByIdQuery,
  } = useCouponDiscountsSlice();
  const [
    createCoupon,
    { isLoading: isCreating, error: createError, data: createData },
  ] = useCreateCouponMutation();
  const [
    updateCoupon,
    { isLoading: isUpdating, error: updateError, data: updateData },
  ] = useUpdateCouponMutation();

  // Get coupon data for editing
  const [
    getCouponById,
    { data: couponData, isLoading: isLoadingCoupon, error: couponError },
  ] = useGetCouponByIdQuery();
  const { toast } = useToast();

  const { useGetSeachUsersQuery } = useUserSlice();
  const { useSearchPackagesQuery } = useDownloadPackageSlice();
  const { useGetActivePaymentMethodsQuery } = usePaymentMethodsSlice();

  const [
    searchPackages,
    { data: searchPackagesData, isLoading: isSearchPackagesLoading },
  ] = useSearchPackagesQuery();

  const [
    getSeachUsers,
    { data: seachUsersData, isLoading: isSeachUsersLoading },
  ] = useGetSeachUsersQuery();

  const [
    getPaymentMethods,
    { data: paymentMethodsData, isLoading: isGetPaymentMethodsLoading },
  ] = useGetActivePaymentMethodsQuery();

  const [codePrefix, setCodePrefix] = useState('');
  const [codeLength, setCodeLength] = useState(8);
  const [copiedCode, setCopiedCode] = useState(false);
  const [isPackageDropdownOpen, setIsPackageDropdownOpen] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState<UserItem[]>([]);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const packageDropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsDropdownOpen(false);
      }
      if (
        packageDropdownRef.current &&
        !packageDropdownRef.current.contains(event.target as Node)
      ) {
        setIsPackageDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Get today's date in the format required by date input
  const getTodayDateString = () => {
    const today = new Date();
    return format(today, 'yyyy-MM-dd');
  };

  const datePresets = getQuickDatePresets();

  // Create a separate schema for edit mode without bulk validation
  const editCouponSchema = z
    .object({
      code: z
        .string()
        .min(1, 'Coupon code is required')
        .max(50, 'Code must be less than 50 characters')
        .regex(
          /^[A-Z0-9_-]+$/,
          'Code must contain only uppercase letters, numbers, hyphens, and underscores',
        ),
      name: z
        .string()
        .min(1, 'Coupon name is required')
        .max(100, 'Name must be less than 100 characters'),
      description: z.string().optional(),
      type: z.enum(['percentage', 'fixed'], {
        required_error: 'Please select a discount type',
      }),
      value: z
        .number()
        .min(0.01, 'Value must be greater than 0')
        .or(z.undefined()),
      minimum_amount: z.number().min(0).optional().or(z.undefined()),
      maximum_discount: z.number().min(0).optional().or(z.undefined()),
      usage_limit: z.number().min(1).optional(),
      applies_to: z.enum(['all', '1', '2'], {
        required_error: 'Please select what this coupon applies to',
      }),
      package_ids: z.array(z.number()).optional(),
      user_targeting: z.enum(
        ['all_users', 'first_time_users', 'selected_users'],
        {
          required_error: 'Please select user targeting',
        },
      ),
      selected_user_ids: z.array(z.number()).optional(),
      user_redemption_limit: z.enum(['once_per_user', 'multiple_per_user'], {
        required_error: 'Please select redemption limit',
      }),
      payment_method_restriction: z.enum(['all', 'selected'], {
        required_error: 'Please select payment method restriction',
      }),
      allowed_payment_methods: z.array(z.string()).optional(),
      is_active: z.boolean().default(true),
      is_public: z.boolean().default(false),
      display_order: z.number().min(0).default(0),
      valid_from: z.string().min(1, 'Valid from date is required'),
      valid_until: z.string().min(1, 'Valid until date is required'),
    })
    .refine(
      data => {
        if (data.type === 'percentage' && data.value && data.value > 100) {
          return false;
        }
        return true;
      },
      {
        message: 'Percentage discount cannot exceed 100%',
        path: ['value'],
      },
    )
    .refine(
      data => {
        if (data.valid_from && data.valid_until) {
          return new Date(data.valid_from) < new Date(data.valid_until);
        }
        return true;
      },
      {
        message: 'Valid until date must be after valid from date',
        path: ['valid_until'],
      },
    )
    .refine(
      data => {
        if (data.value === undefined || data.value === null) {
          return false;
        }
        return true;
      },
      {
        message: 'Discount value is required',
        path: ['value'],
      },
    )
    .refine(
      data => {
        if (
          data.applies_to === '2' &&
          (!data.package_ids || data.package_ids.length === 0)
        ) {
          return false;
        }
        return true;
      },
      {
        message:
          'Please select a package when applying to subscription packages',
        path: ['package_ids'],
      },
    )
    .refine(
      data => {
        if (
          data.user_targeting === 'selected_users' &&
          (!data.selected_user_ids || data.selected_user_ids.length === 0)
        ) {
          return false;
        }
        return true;
      },
      {
        message:
          'Please select at least one user when targeting selected users',
        path: ['selected_user_ids'],
      },
    )
    .refine(
      data => {
        if (
          data.payment_method_restriction === 'selected' &&
          (!data.allowed_payment_methods ||
            data.allowed_payment_methods.length === 0)
        ) {
          return false;
        }
        return true;
      },
      {
        message: 'Please select at least one payment method',
        path: ['allowed_payment_methods'],
      },
    );

  const dynamicCouponSchema = isEdit ? editCouponSchema : couponSchema;

  const form = useForm<CouponFormData>({
    resolver: zodResolver(dynamicCouponSchema),
    defaultValues: {
      code: '',
      name: '',
      description: '',
      type: 'percentage',
      value: undefined,
      minimum_amount: undefined,
      maximum_discount: undefined,
      usage_limit: undefined,
      applies_to: 'all',
      package_ids: [],
      user_targeting: 'all_users',
      selected_user_ids: [],
      user_redemption_limit: 'once_per_user',
      bulk_generate: false,
      bulk_count: undefined,
      payment_method_restriction: 'all',
      allowed_payment_methods: [],
      is_active: true,
      is_public: false,
      display_order: 0,
      valid_from: getTodayDateString(),
      valid_until: getTodayDateString(),
    },
  });

  // Watch form values for preview
  // const watchedValues = form.watch();

  const onSubmit = async (values: CouponFormData) => {
    try {
      // Ensure value is defined before submitting
      if (values.value === undefined) {
        toast({
          title: 'Error',
          description: 'Discount value is required',
          variant: 'destructive',
        });
        return;
      }

      const payload = {
        ...values,
        code: values.code.toUpperCase(),
        // Only include package_ids if applies_to is '2'
        package_ids: values.applies_to === '2' ? values.package_ids : undefined,
        // Only include selected_user_ids if user_targeting is 'selected_users'
        selected_user_ids:
          values.user_targeting === 'selected_users'
            ? values.selected_user_ids
            : undefined,
        // Only include allowed_payment_methods if payment_method_restriction is 'selected'
        allowed_payment_methods:
          values.payment_method_restriction === 'selected'
            ? values.allowed_payment_methods
            : undefined,
        // Only include display_order if is_public is true
        display_order: values.is_public ? values.display_order : undefined,
        // Only include bulk_count if bulk_generate is true
        bulk_count: values.bulk_generate ? values.bulk_count : undefined,
      };

      if (isEdit && id) {
        await updateCoupon({ id: parseInt(id), ...payload } as any).unwrap();
      } else {
        await createCoupon(payload as any).unwrap();
      }
    } catch (err) {
      console.error('Error saving coupon:', err);
    }
  };

  // Handle successful creation/update
  useEffect(() => {
    if (createData || updateData) {
      toast({
        title: 'Success',
        description: isEdit
          ? 'Coupon updated successfully'
          : 'Coupon created successfully',
        variant: 'success',
      });
      navigate('/coupon-discounts');
    }
  }, [createData, updateData, toast, isEdit, navigate]);

  // Handle errors
  useEffect(() => {
    const error = createError || updateError;
    if (error) {
      toast({
        title: 'Error',
        description:
          (error as any)?.data?.message ||
          `Failed to ${isEdit ? 'update' : 'create'} coupon`,
        variant: 'destructive',
      });
    }
  }, [createError, updateError, toast, isEdit]);

  // Handle coupon fetch errors
  useEffect(() => {
    if (couponError && isEdit) {
      toast({
        title: 'Error',
        description: 'Failed to load coupon data',
        variant: 'destructive',
      });
    }
  }, [couponError, isEdit, toast]);

  // Watch for changes in valid_from and update valid_until if needed
  const validFrom = form.watch('valid_from');
  useEffect(() => {
    const validUntil = form.watch('valid_until');

    if (validFrom && validUntil && new Date(validFrom) > new Date(validUntil)) {
      form.setValue('valid_until', validFrom);
    }
  }, [validFrom, form]);

  // Utility functions
  const handleGenerateCode = () => {
    const newCode = generateCouponCode(codePrefix, codeLength);
    form.setValue('code', newCode);
    setCopiedCode(false);
  };

  const handleCopyCode = async () => {
    const code = form.getValues('code');
    if (code) {
      try {
        await navigator.clipboard.writeText(code);
        setCopiedCode(true);
        toast({
          title: 'Copied!',
          description: 'Coupon code copied to clipboard',
          variant: 'default',
        });
        setTimeout(() => setCopiedCode(false), 2000);
      } catch (err) {
        console.error('Failed to copy code:', err);
      }
    }
  };

  const handleQuickDate = (
    field: 'valid_from' | 'valid_until',
    preset: string,
  ) => {
    form.setValue(field, preset);
  };

  const handleQuickDuration = (days: number) => {
    const validFrom = form.getValues('valid_from');
    if (validFrom) {
      const fromDate = new Date(validFrom);
      const untilDate = addDays(fromDate, days);
      form.setValue('valid_until', format(untilDate, 'yyyy-MM-dd'));
    }
  };

  useEffect(() => {
    searchPackages({ search: '' } as any);
    getPaymentMethods({
      search: '',
      page: 1,
      limit: 10,
    } as any);
    getSeachUsers({
      search: '',
      page: 1,
      limit: 10,
    });
  }, [searchPackages, getPaymentMethods, getSeachUsers]);

  // Update users when search data changes

  // Check if data is loading
  const isLoading =
    isCreating ||
    isUpdating ||
    isLoadingCoupon ||
    isSearchPackagesLoading ||
    isSeachUsersLoading ||
    isGetPaymentMethodsLoading;

  // Handle initial loading state and fetch coupon data for editing
  useEffect(() => {
    if (isEdit && id) {
      getCouponById(id);
    }
  }, [isEdit, id, getCouponById]);

  // Pre-fill form with fetched coupon data
  useEffect(() => {
    if (couponData && isEdit) {
      // Handle the nested data structure from API response
      const coupon = couponData.data || couponData;

      // Populate selectedUsers state with API data
      if (
        (coupon as any).selected_users &&
        Array.isArray((coupon as any).selected_users)
      ) {
        const users = (coupon as any).selected_users.map((user: any) => ({
          id: user.user_id,
          name: user.name || user.username,
          email: user.email,
        }));
        setSelectedUsers(users);
      }

      form.reset({
        code: coupon.code || '',
        name: coupon.name || '',
        description: coupon.description || '',
        type: coupon.type || 'percentage',
        value: coupon.value ? parseFloat(coupon.value) : undefined,
        minimum_amount: coupon.minimum_amount
          ? parseFloat(coupon.minimum_amount)
          : undefined,
        maximum_discount: coupon.maximum_discount
          ? parseFloat(coupon.maximum_discount)
          : undefined,
        usage_limit: coupon.usage_limit || undefined,
        applies_to: (coupon.applies_to as 'all' | '1' | '2') || 'all',
        package_ids: coupon.package_ids || [],
        user_targeting:
          (coupon.user_targeting as
            | 'all_users'
            | 'first_time_users'
            | 'selected_users') || 'all_users',
        selected_user_ids: coupon.selected_user_ids || [],
        user_redemption_limit:
          (coupon.user_redemption_limit as
            | 'once_per_user'
            | 'multiple_per_user') || 'once_per_user',
        bulk_generate: Boolean(coupon.is_bulk_generated),
        bulk_count: undefined, // This might need to be calculated or fetched separately
        payment_method_restriction:
          (coupon.payment_method_restriction as 'all' | 'selected') || 'all',
        allowed_payment_methods: coupon.allowed_payment_methods || [],
        is_active: Boolean(coupon.is_active),
        is_public: Boolean(coupon.is_public),
        display_order: coupon.display_order || 0,
        valid_from: coupon.valid_from || getTodayDateString(),
        valid_until: coupon.valid_until || getTodayDateString(),
      });

      // Force update the user_targeting field specifically
      if (coupon.user_targeting) {
        form.setValue(
          'user_targeting',
          coupon.user_targeting as
            | 'all_users'
            | 'first_time_users'
            | 'selected_users',
        );
      }
    }
  }, [couponData, isEdit, form]);

  // Handle form errors
  const formErrors = form.formState.errors;
  const hasFormErrors = Object.keys(formErrors).length > 0;

  return (
    <div className="container mx-auto p-6 relative">
      {isLoading && <LoadingState />}
      {isEdit && isLoadingCoupon && (
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="flex flex-col items-center gap-4">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
            <p className="text-muted-foreground">Loading coupon data...</p>
          </div>
        </div>
      )}
      {hasFormErrors && (
        <Alert variant="destructive" className="mb-6">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            Please fix the form errors before continuing
          </AlertDescription>
        </Alert>
      )}
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-4 mb-6">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/coupon-discounts')}
            className="flex items-center gap-2 hover:bg-gray-100 transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Coupons
          </Button>
        </div>
        <div className="space-y-2">
          <h1 className="text-lg font-semibold text-gray-900 tracking-tight m-0">
            {isEdit ? 'Edit Coupon' : 'Create New Coupon'}
          </h1>
          <p className="text-[13px] text-gray-600">
            {isEdit
              ? 'Update coupon details and settings'
              : 'Create a new discount coupon for your customers'}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Form */}
        <div className="lg:col-span-2">
          <Card className="border-0">
            <CardHeader className="space-y-1 pb-4">
              <CardTitle className="text-base font-semibold m-0">
                Coupon Details
              </CardTitle>
              <CardDescription className="text-[13px]">
                Fill in the coupon information and settings
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-8 mt-4">
              <Form {...form}>
                <form
                  onSubmit={form.handleSubmit(onSubmit)}
                  className="space-y-6"
                >
                  {/* Basic Information */}
                  <div className="space-y-6">
                    <h3 className="text-[13px] font-semibold flex items-center gap-2 pb-2 border-b">
                      Basic Information
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <FormField
                        control={form.control}
                        name="code"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Coupon Code *</FormLabel>
                            <div className="flex gap-2">
                              <FormControl>
                                <Input
                                  placeholder="SAVE20"
                                  {...field}
                                  onChange={e =>
                                    field.onChange(e.target.value.toUpperCase())
                                  }
                                  className="flex-1"
                                />
                              </FormControl>
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={handleCopyCode}
                                disabled={!field.value}
                                className="px-3"
                              >
                                <Copy
                                  className={`h-4 w-4 ${
                                    copiedCode ? 'text-green-500' : ''
                                  }`}
                                />
                              </Button>
                            </div>
                            <div className="flex gap-2 mt-2">
                              <Input
                                placeholder="PREFIX"
                                value={codePrefix}
                                onChange={e =>
                                  setCodePrefix(e.target.value.toUpperCase())
                                }
                                className="w-32 font-mono text-[13px]"
                              />
                              <Select
                                value={codeLength.toString()}
                                onValueChange={value =>
                                  setCodeLength(parseInt(value))
                                }
                              >
                                <SelectTrigger className="w-20">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="6">6</SelectItem>
                                  <SelectItem value="8">8</SelectItem>
                                  <SelectItem value="10">10</SelectItem>
                                  <SelectItem value="12">12</SelectItem>
                                </SelectContent>
                              </Select>
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={handleGenerateCode}
                                className="flex items-center gap-1"
                              >
                                <RefreshCw className="h-3 w-3" />
                                Generate
                              </Button>
                            </div>
                            <FormDescription>
                              Use uppercase letters, numbers, hyphens, and
                              underscores only
                            </FormDescription>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="name"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Coupon Name *</FormLabel>
                            <FormControl>
                              <Input placeholder="20% Off Sale" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>

                    <FormField
                      control={form.control}
                      name="description"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Description</FormLabel>
                          <FormControl>
                            <Textarea
                              placeholder="Optional description for this coupon"
                              className="min-h-[100px]"
                              {...field}
                            />
                          </FormControl>
                          <FormDescription>
                            Provide additional details about this coupon
                          </FormDescription>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <Separator />

                  {/* Discount Settings */}
                  <div className="space-y-6">
                    <h3 className="text-[13px] font-semibold flex items-center gap-2 pb-2 border-b">
                      Discount Settings
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <FormField
                        control={form.control}
                        name="type"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Discount Type *</FormLabel>
                            <Select
                              onValueChange={field.onChange}
                              value={field.value}
                            >
                              <FormControl>
                                <SelectTrigger>
                                  <SelectValue placeholder="Select discount type" />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                <SelectItem value="percentage">
                                  <div className="flex items-center gap-2">
                                    <Percent className="h-4 w-4" />
                                    Percentage
                                  </div>
                                </SelectItem>
                                <SelectItem value="fixed">
                                  <div className="flex items-center gap-2">
                                    <DollarSign className="h-4 w-4" />
                                    Fixed Amount
                                  </div>
                                </SelectItem>
                              </SelectContent>
                            </Select>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="value"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>
                              Discount Value *
                              {form.watch('type') === 'percentage'
                                ? ' (%)'
                                : ' ($)'}
                            </FormLabel>
                            <FormControl>
                              <Input
                                type="number"
                                step="0.01"
                                min="0"
                                max={
                                  form.watch('type') === 'percentage'
                                    ? 100
                                    : undefined
                                }
                                placeholder={
                                  form.watch('type') === 'percentage'
                                    ? '20'
                                    : '10.00'
                                }
                                value={field.value || ''}
                                onChange={e => {
                                  const value = e.target.value;
                                  field.onChange(
                                    value === ''
                                      ? undefined
                                      : parseFloat(value),
                                  );
                                }}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <FormField
                        control={form.control}
                        name="minimum_amount"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Minimum Order Amount ($)</FormLabel>
                            <FormControl>
                              <Input
                                type="number"
                                step="0.01"
                                min="0"
                                placeholder="50.00"
                                value={field.value || ''}
                                onChange={e => {
                                  const value = e.target.value;
                                  field.onChange(
                                    value === ''
                                      ? undefined
                                      : parseFloat(value),
                                  );
                                }}
                              />
                            </FormControl>
                            <FormDescription>
                              Minimum order amount required to use this coupon
                            </FormDescription>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="maximum_discount"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Maximum Discount ($)</FormLabel>
                            <FormControl>
                              <Input
                                type="number"
                                step="0.01"
                                min="0"
                                placeholder="100.00"
                                value={field.value || ''}
                                onChange={e => {
                                  const value = e.target.value;
                                  field.onChange(
                                    value === ''
                                      ? undefined
                                      : parseFloat(value),
                                  );
                                }}
                              />
                            </FormControl>
                            <FormDescription>
                              Maximum discount amount (for percentage coupons)
                            </FormDescription>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>

                    <FormField
                      control={form.control}
                      name="usage_limit"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Usage Limit</FormLabel>
                          <FormControl>
                            <Input
                              type="number"
                              min="1"
                              placeholder="100 (leave empty for unlimited)"
                              value={field.value || ''}
                              onChange={e => {
                                const value = e.target.value;
                                field.onChange(
                                  value === '' ? undefined : parseInt(value),
                                );
                              }}
                            />
                          </FormControl>
                          <FormDescription>
                            Maximum number of times this coupon can be used
                          </FormDescription>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <Separator />

                  {/* Applies To Settings */}
                  <div className="space-y-6">
                    <h3 className="text-[13px] font-semibold flex items-center gap-2 pb-2 border-b">
                      Applies To
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <FormField
                        control={form.control}
                        name="applies_to"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Applies To *</FormLabel>
                            <Select
                              onValueChange={value => {
                                field.onChange(value);
                                // Clear package_ids when not selecting subscription package
                                if (value !== '2') {
                                  form.setValue('package_ids', []);
                                }
                              }}
                              value={field.value}
                            >
                              <FormControl>
                                <SelectTrigger>
                                  <SelectValue placeholder="Select what this coupon applies to" />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                <SelectItem value="all">
                                  <div className="flex items-center gap-2">
                                    <Package className="h-4 w-4" />
                                    All
                                  </div>
                                </SelectItem>
                                <SelectItem value="2">
                                  <div className="flex items-center gap-2">
                                    <Package className="h-4 w-4" />
                                    Subscription Package
                                  </div>
                                </SelectItem>
                                <SelectItem value="1">
                                  <div className="flex items-center gap-2">
                                    <FileIcon className="h-4 w-4" />
                                    Digital Files
                                  </div>
                                </SelectItem>
                              </SelectContent>
                            </Select>
                            <FormDescription>
                              Choose what products this coupon can be applied to
                            </FormDescription>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      {form.watch('applies_to') === '2' && (
                        <FormField
                          control={form.control}
                          name="package_ids"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Select Package *</FormLabel>
                              <div className="relative">
                                <Button
                                  type="button"
                                  variant="outline"
                                  className="w-full justify-between"
                                  onClick={() =>
                                    setIsPackageDropdownOpen(
                                      !isPackageDropdownOpen,
                                    )
                                  }
                                >
                                  <span className="flex items-center gap-2">
                                    <Package className="h-4 w-4" />
                                    <span>
                                      {(field.value || []).length > 0
                                        ? `${
                                            (field.value || []).length
                                          } Package${
                                            (field.value || []).length > 1
                                              ? 's'
                                              : ''
                                          } Selected`
                                        : 'Select Packages'}
                                    </span>
                                  </span>
                                  <ChevronDown
                                    className={`h-4 w-4 transition-transform ${
                                      isPackageDropdownOpen ? 'rotate-180' : ''
                                    }`}
                                  />
                                </Button>

                                {isPackageDropdownOpen && (
                                  <div className="absolute z-50 w-full mt-1 bg-white rounded-md shadow-lg border">
                                    <div className="p-2 border-b">
                                      <Input
                                        type="text"
                                        placeholder="Search packages..."
                                        className="w-full"
                                        onChange={e => {
                                          searchPackages({
                                            search: e.target.value,
                                          } as any);
                                        }}
                                      />
                                    </div>
                                    <div className="max-h-60 overflow-auto">
                                      {!isSearchPackagesLoading && (
                                        <>
                                          <div
                                            className="px-3 py-2 hover:bg-gray-100 cursor-pointer flex items-center gap-2 border-b text-[13px]"
                                            onClick={() => {
                                              if (searchPackagesData?.data) {
                                                const allIds =
                                                  searchPackagesData.data.map(
                                                    p => p.id,
                                                  );
                                                field.onChange(
                                                  (field.value || []).length ===
                                                    allIds.length
                                                    ? []
                                                    : allIds,
                                                );
                                                setIsPackageDropdownOpen(false);
                                              }
                                            }}
                                          >
                                            <div className="flex-1">
                                              <div className="text-[13px]">
                                                All Packages
                                              </div>
                                            </div>
                                            {searchPackagesData?.data &&
                                              (field.value || []).length ===
                                                searchPackagesData.data
                                                  .length && (
                                                <Check className="h-4 w-4 text-primary" />
                                              )}
                                          </div>
                                          {searchPackagesData?.data?.map(
                                            pkg => (
                                              <div
                                                key={pkg.id}
                                                className="px-3 py-2 hover:bg-gray-100 cursor-pointer flex items-center gap-2 text-[13px]"
                                                onClick={() => {
                                                  const newValue =
                                                    field.value || [];
                                                  const isSelected =
                                                    newValue.includes(pkg.id);
                                                  field.onChange(
                                                    isSelected
                                                      ? newValue.filter(
                                                          id => id !== pkg.id,
                                                        )
                                                      : [...newValue, pkg.id],
                                                  );
                                                  setIsPackageDropdownOpen(
                                                    false,
                                                  );
                                                }}
                                              >
                                                <div className="flex-1">
                                                  <div className="text-[13px]">
                                                    {pkg.title}
                                                  </div>
                                                </div>
                                                {(field.value || []).includes(
                                                  pkg.id,
                                                ) && (
                                                  <Check className="h-4 w-4 text-primary" />
                                                )}
                                              </div>
                                            ),
                                          )}
                                        </>
                                      )}
                                      {isSearchPackagesLoading && (
                                        <div className="p-4 text-center">
                                          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary mx-auto"></div>
                                          <div className="mt-2 text-[13px] text-gray-500">
                                            Loading...
                                          </div>
                                        </div>
                                      )}
                                      {!isSearchPackagesLoading &&
                                        (!searchPackagesData?.data ||
                                          searchPackagesData.data.length ===
                                            0) && (
                                          <div className="p-4 text-center text-[13px] text-gray-500">
                                            No packages found
                                          </div>
                                        )}
                                    </div>
                                  </div>
                                )}
                              </div>
                              {(field.value || []).length > 0 && (
                                <div className="flex flex-wrap gap-2 mt-2">
                                  {searchPackagesData?.data
                                    ?.filter(pkg =>
                                      (field.value || []).includes(pkg.id),
                                    )
                                    .map(pkg => (
                                      <div
                                        key={pkg.id}
                                        className="flex items-center gap-2 bg-gray-100 rounded-md px-3 py-1"
                                      >
                                        <span className="text-[13px]">
                                          {pkg.title}
                                        </span>
                                        <Button
                                          type="button"
                                          variant="ghost"
                                          size="sm"
                                          className="h-auto p-1 text-red-500 hover:bg-red-50"
                                          onClick={() => {
                                            field.onChange(
                                              (field.value || []).filter(
                                                id => id !== pkg.id,
                                              ),
                                            );
                                          }}
                                        >
                                          <X className="h-4 w-4" />
                                        </Button>
                                      </div>
                                    ))}
                                </div>
                              )}
                              <FormDescription>
                                Choose which subscription package this coupon
                                applies to
                              </FormDescription>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      )}
                    </div>
                  </div>

                  <Separator />

                  {/* User Targeting Settings */}
                  <div className="space-y-6">
                    <h3 className="text-[13px] font-semibold flex items-center gap-2 pb-2 border-b">
                      User Targeting
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <FormField
                        control={form.control}
                        name="user_targeting"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Target Users *</FormLabel>
                            <Select
                              onValueChange={value => {
                                field.onChange(value);
                                // Clear selected users when not selecting specific users
                                if (value !== 'selected_users') {
                                  form.setValue('selected_user_ids', []);
                                  setSelectedUsers([]);
                                }
                              }}
                              value={field.value}
                            >
                              <FormControl>
                                <SelectTrigger>
                                  <SelectValue placeholder="Select user targeting" />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                <SelectItem value="all_users">
                                  <div className="flex items-center gap-2">
                                    <Users className="h-4 w-4" />
                                    All Users
                                  </div>
                                </SelectItem>
                                <SelectItem value="first_time_users">
                                  <div className="flex items-center gap-2">
                                    <User className="h-4 w-4" />
                                    First Time Users
                                  </div>
                                </SelectItem>
                                <SelectItem value="selected_users">
                                  <div className="flex items-center gap-2">
                                    <UserCheck className="h-4 w-4" />
                                    Selected Users
                                  </div>
                                </SelectItem>
                              </SelectContent>
                            </Select>
                            <FormDescription>
                              Choose which users can use this coupon
                            </FormDescription>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      {form.watch('user_targeting') === 'selected_users' && (
                        <FormField
                          control={form.control}
                          name="selected_user_ids"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Select Users *</FormLabel>
                              <div className="space-y-2">
                                <div className="relative" ref={dropdownRef}>
                                  <Button
                                    type="button"
                                    variant="outline"
                                    className="w-full justify-between"
                                    onClick={() =>
                                      setIsDropdownOpen(!isDropdownOpen)
                                    }
                                  >
                                    <span className="flex items-center gap-2">
                                      <UserCheck className="h-4 w-4" />
                                      <span>
                                        {selectedUsers.length
                                          ? `${selectedUsers.length} User${
                                              selectedUsers.length > 1
                                                ? 's'
                                                : ''
                                            } Selected`
                                          : 'Select Users'}
                                      </span>
                                    </span>
                                    <ChevronDown
                                      className={`h-4 w-4 transition-transform ${
                                        isDropdownOpen ? 'rotate-180' : ''
                                      }`}
                                    />
                                  </Button>

                                  {isDropdownOpen && (
                                    <div className="absolute z-50 w-full mt-1 bg-white rounded-md shadow-lg border">
                                      <div className="p-2 border-b">
                                        <Input
                                          type="text"
                                          placeholder="Search users..."
                                          className="w-full"
                                          onChange={e => {
                                            getSeachUsers({
                                              search: e.target.value,
                                              page: 1,
                                              limit: 10,
                                            });
                                          }}
                                        />
                                      </div>
                                      <div className="max-h-60 overflow-auto">
                                        {!isSeachUsersLoading &&
                                          seachUsersData?.response?.data
                                            ?.filter(
                                              user =>
                                                !(field.value || []).includes(
                                                  user.user_id,
                                                ),
                                            )
                                            .map(user => (
                                              <div
                                                key={user.user_id}
                                                className="px-3 py-2 hover:bg-gray-100 cursor-pointer flex items-center gap-2 text-[13px]"
                                                onClick={() => {
                                                  const newIds = [
                                                    ...(field.value || []),
                                                    user.user_id,
                                                  ];
                                                  field.onChange(newIds);
                                                  setSelectedUsers([
                                                    ...selectedUsers,
                                                    {
                                                      id: user.user_id,
                                                      name: user.first_name,
                                                      email: user.email,
                                                    },
                                                  ]);
                                                  setIsDropdownOpen(false);
                                                }}
                                              >
                                                <div className="flex-1">
                                                  <div className="text-[13px] font-medium">
                                                    {user.first_name}
                                                  </div>
                                                  <div className="text-[11px] text-gray-500">
                                                    {user.email}
                                                  </div>
                                                </div>
                                                <Plus className="h-4 w-4 text-gray-400 hover:text-primary" />
                                              </div>
                                            ))}
                                        {isSeachUsersLoading && (
                                          <div className="p-4 text-center">
                                            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary mx-auto"></div>
                                            <div className="mt-2 text-[13px] text-gray-500">
                                              Loading...
                                            </div>
                                          </div>
                                        )}
                                        {!isSeachUsersLoading &&
                                          (!seachUsersData?.response?.data ||
                                            seachUsersData.response.data
                                              .length === 0) && (
                                            <div className="p-4 text-center text-[13px] text-gray-500">
                                              No users found
                                            </div>
                                          )}
                                      </div>
                                    </div>
                                  )}
                                </div>
                                {selectedUsers.length > 0 && (
                                  <div className="flex flex-wrap gap-2 mt-2">
                                    {selectedUsers.map(user => (
                                      <div
                                        key={user.id}
                                        className="flex items-center gap-2 bg-gray-100 rounded-md px-3 py-1"
                                      >
                                        <span className="text-[13px]">
                                          {user.name} ({user.email})
                                        </span>
                                        <Button
                                          type="button"
                                          variant="ghost"
                                          size="sm"
                                          className="h-auto p-1 text-red-500 hover:bg-red-50"
                                          onClick={() => {
                                            const newSelectedUsers =
                                              selectedUsers.filter(
                                                u => u.id !== user.id,
                                              );
                                            setSelectedUsers(newSelectedUsers);
                                            field.onChange(
                                              newSelectedUsers.map(u => u.id),
                                            );
                                          }}
                                        >
                                          <X className="h-4 w-4" />
                                        </Button>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                              <FormDescription>
                                Choose specific users who can use this coupon
                              </FormDescription>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      )}
                    </div>
                  </div>

                  {/* User Redemption Limit */}
                  <div className="mt-4">
                    <FormField
                      control={form.control}
                      name="user_redemption_limit"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>User Redemption Limit *</FormLabel>
                          <Select
                            onValueChange={field.onChange}
                            value={field.value}
                          >
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue placeholder="Select redemption limit" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              <SelectItem value="once_per_user">
                                <div className="flex items-center gap-2">
                                  <User className="h-4 w-4" />
                                  Once Per User
                                </div>
                              </SelectItem>
                              <SelectItem value="multiple_per_user">
                                <div className="flex items-center gap-2">
                                  <Users className="h-4 w-4" />
                                  Multiple Times Per User
                                </div>
                              </SelectItem>
                            </SelectContent>
                          </Select>
                          <FormDescription>
                            Control how many times each user can redeem this
                            coupon
                          </FormDescription>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <Separator />

                  {/* Validity Settings */}
                  <div className="space-y-6">
                    <h3 className="text-[13px] font-semibold flex items-center gap-2 pb-2 border-b">
                      Validity Settings
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <FormField
                        control={form.control}
                        name="valid_from"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Valid From *</FormLabel>
                            <FormControl>
                              <Input type="date" {...field} />
                            </FormControl>
                            <div className="flex flex-wrap gap-1 mt-2">
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={() =>
                                  handleQuickDate(
                                    'valid_from',
                                    datePresets.today,
                                  )
                                }
                                className="text-xs px-2 py-1 h-6 shadow-none"
                              >
                                Today
                              </Button>
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={() =>
                                  handleQuickDate(
                                    'valid_from',
                                    datePresets.tomorrow,
                                  )
                                }
                                className="text-xs px-2 py-1 h-6 shadow-none"
                              >
                                Tomorrow
                              </Button>
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={() =>
                                  handleQuickDate(
                                    'valid_from',
                                    datePresets.nextWeek,
                                  )
                                }
                                className="text-xs px-2 py-1 h-6 shadow-none"
                              >
                                Next Week
                              </Button>
                            </div>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="valid_until"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Valid Until *</FormLabel>
                            <FormControl>
                              <Input
                                type="date"
                                min={
                                  form.watch('valid_from') ||
                                  getTodayDateString()
                                }
                                {...field}
                              />
                            </FormControl>
                            <div className="flex flex-wrap gap-1 mt-2">
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={() => handleQuickDuration(7)}
                                className="text-xs px-2 py-1 h-6 shadow-none"
                              >
                                1 Week
                              </Button>
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={() => handleQuickDuration(30)}
                                className="text-xs px-2 py-1 h-6 shadow-none"
                              >
                                1 Month
                              </Button>
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={() => handleQuickDuration(90)}
                                className="text-xs px-2 py-1 h-6 shadow-none"
                              >
                                3 Months
                              </Button>
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={() => handleQuickDuration(365)}
                                className="text-xs px-2 py-1 h-6 shadow-none"
                              >
                                1 Year
                              </Button>
                            </div>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>

                    <FormField
                      control={form.control}
                      name="is_active"
                      render={({ field }) => (
                        <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                          <div className="space-y-0.5">
                            <FormLabel className="text-[13px]">
                              Active Status
                            </FormLabel>
                            <div className="text-[13px] text-muted-foreground">
                              Enable this coupon for use
                            </div>
                          </div>
                          <FormControl>
                            <Switch
                              checked={field.value}
                              onCheckedChange={field.onChange}
                            />
                          </FormControl>
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="is_public"
                      render={({ field }) => (
                        <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                          <div className="space-y-0.5">
                            <FormLabel className="text-[13px]">
                              Public Visibility
                            </FormLabel>
                            <div className="text-[13px] text-muted-foreground">
                              Show this coupon on the website for all users to
                              see
                            </div>
                          </div>
                          <FormControl>
                            <Switch
                              checked={field.value}
                              onCheckedChange={field.onChange}
                            />
                          </FormControl>
                        </FormItem>
                      )}
                    />

                    {form.watch('is_public') && (
                      <div className="space-y-4 rounded-lg border p-4 mt-4">
                        <FormField
                          control={form.control}
                          name="display_order"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Display Order</FormLabel>
                              <FormDescription>
                                Order in which this coupon appears (0 = end,
                                higher numbers appear first)
                              </FormDescription>
                              <FormControl>
                                <Input
                                  type="number"
                                  min="0"
                                  {...field}
                                  onChange={e =>
                                    field.onChange(Number(e.target.value))
                                  }
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>
                    )}
                  </div>

                  <Separator />

                  {/* Payment Method Settings */}
                  <div className="space-y-6">
                    <h3 className="text-[13px] font-semibold flex items-center gap-2 pb-2 border-b">
                      Payment Method Restrictions
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <FormField
                        control={form.control}
                        name="payment_method_restriction"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Payment Method Restriction *</FormLabel>
                            <Select
                              onValueChange={value => {
                                field.onChange(value);
                                if (value !== 'selected') {
                                  form.setValue('allowed_payment_methods', []);
                                }
                              }}
                              value={field.value}
                            >
                              <FormControl>
                                <SelectTrigger>
                                  <SelectValue placeholder="Select restriction type" />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                <SelectItem value="all">
                                  <div className="flex items-center gap-2">
                                    <CreditCard className="h-4 w-4" />
                                    All Payment Methods
                                  </div>
                                </SelectItem>
                                <SelectItem value="selected">
                                  <div className="flex items-center gap-2">
                                    <Wallet className="h-4 w-4" />
                                    Selected Payment Methods
                                  </div>
                                </SelectItem>
                              </SelectContent>
                            </Select>
                            <FormDescription>
                              Choose which payment methods can use this coupon
                            </FormDescription>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      {form.watch('payment_method_restriction') ===
                        'selected' && (
                        <FormField
                          control={form.control}
                          name="allowed_payment_methods"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Select Payment Methods *</FormLabel>
                              <div className="grid grid-cols-2 gap-2">
                                {paymentMethodsData?.gateways.map(gateway => (
                                  <div
                                    key={gateway.gateway_id}
                                    className={`flex items-center gap-2 p-2 rounded border cursor-pointer transition-colors ${
                                      field.value?.includes(
                                        gateway.gateway_type,
                                      )
                                        ? 'bg-primary/10 border-primary'
                                        : 'hover:bg-muted'
                                    }`}
                                    onClick={() => {
                                      const currentValue = field.value || [];
                                      const newValue = currentValue.includes(
                                        gateway.gateway_type,
                                      )
                                        ? currentValue.filter(
                                            type =>
                                              type !== gateway.gateway_type,
                                          )
                                        : [
                                            ...currentValue,
                                            gateway.gateway_type,
                                          ];
                                      field.onChange(newValue);
                                    }}
                                  >
                                    <div className="flex items-center gap-2">
                                      <img
                                        src={`${endpoints.mediaBaseUrl}/images/payment-gateway/${gateway.icon}`}
                                        alt={gateway.name}
                                        onError={e => {
                                          const target = e.currentTarget;
                                          target.style.display = 'none';
                                        }}
                                        className="h-6 w-6 object-contain"
                                      />
                                      <span className="text-[13px] font-medium">
                                        {gateway.name}
                                      </span>
                                    </div>
                                  </div>
                                ))}
                              </div>
                              <FormDescription>
                                Select which payment methods can use this coupon
                              </FormDescription>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      )}
                    </div>
                  </div>

                  <Separator />

                  {/* Bulk Generation Settings - Only show for new coupons */}
                  {!isEdit && (
                    <div className="space-y-6">
                      <h3 className="text-[13px] font-semibold flex items-center gap-2 pb-2 border-b">
                        Bulk Generation
                      </h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <FormField
                          control={form.control}
                          name="bulk_generate"
                          render={({ field }) => (
                            <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                              <div className="space-y-0.5">
                                <FormLabel className="text-[13px]">
                                  Bulk Generate
                                </FormLabel>
                                <div className="text-[13px] text-muted-foreground">
                                  Generate multiple coupons with same settings
                                </div>
                              </div>
                              <FormControl>
                                <Switch
                                  checked={field.value}
                                  onCheckedChange={checked => {
                                    field.onChange(checked);
                                    if (!checked) {
                                      form.setValue('bulk_count', undefined);
                                    }
                                  }}
                                />
                              </FormControl>
                            </FormItem>
                          )}
                        />

                        {form.watch('bulk_generate') && (
                          <FormField
                            control={form.control}
                            name="bulk_count"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>Number of Coupons *</FormLabel>
                                <FormControl>
                                  <Input
                                    type="number"
                                    min="1"
                                    max="1000"
                                    placeholder="Enter number of coupons (1-1000)"
                                    value={field.value || ''}
                                    onChange={e => {
                                      const value = e.target.value;
                                      field.onChange(
                                        value === ''
                                          ? undefined
                                          : parseInt(value),
                                      );
                                    }}
                                  />
                                </FormControl>
                                <FormDescription>
                                  Generate 1-1000 coupons with unique codes
                                </FormDescription>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                        )}
                      </div>
                    </div>
                  )}

                  {/* Action Buttons */}
                  <div className="flex justify-between items-center gap-4 pt-8 border-t">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => {
                        const templateData = form.getValues();
                        localStorage.setItem(
                          'couponTemplate',
                          JSON.stringify(templateData),
                        );
                        toast({
                          title: 'Template Saved',
                          description: 'Coupon settings saved as template',
                          variant: 'default',
                        });
                      }}
                      className="flex items-center gap-2 hover:bg-blue-50 text-blue-600 border-blue-200"
                    >
                      <BookmarkPlus className="h-4 w-4" />
                      Save as Template
                    </Button>
                    <div className="flex gap-4">
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => navigate('/coupon-discounts')}
                        className="min-w-[100px] hover:bg-gray-100 transition-colors"
                      >
                        Cancel
                      </Button>
                      <Button
                        type="submit"
                        disabled={isLoading}
                        className="min-w-[140px] bg-primary transition-all  hover:"
                      >
                        {isLoading ? (
                          <>
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                            {isEdit ? 'Updating...' : 'Creating...'}
                          </>
                        ) : (
                          <>
                            <Save className="h-4 w-4 mr-2" />
                            {isEdit ? 'Update Coupon' : 'Create Coupon'}
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                </form>
              </Form>
            </CardContent>
          </Card>
        </div>

        <div className="lg:col-span-1">
          {/* Coupon Summary */}
          <Card className="">
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <FileIcon className="h-5 w-5" />
                Coupon Summary
              </CardTitle>
              <CardDescription>Current coupon configuration</CardDescription>
            </CardHeader>
            <CardContent className="space-y-2">
              <div className="grid gap-3 text-sm">
                <div className="flex justify-between items-center py-1 border-b">
                  <span className="text-muted-foreground">Coupon Code:</span>
                  <span className="font-mono font-medium">
                    {form.watch('code') || '-'}
                  </span>
                </div>
                <div className="flex justify-between items-center py-1 border-b">
                  <span className="text-muted-foreground">Name:</span>
                  <span className="font-medium">
                    {form.watch('name') || '-'}
                  </span>
                </div>
                <div className="flex justify-between items-center py-1 border-b">
                  <span className="text-muted-foreground">Description:</span>
                  <span className="font-medium">
                    {form.watch('description') || '-'}
                  </span>
                </div>
                <div className="flex justify-between items-center py-1 border-b">
                  <span className="text-muted-foreground">Discount:</span>
                  <span className="font-medium">
                    {form.watch('type') === 'percentage'
                      ? `${form.watch('value') || 0}%`
                      : `$${form.watch('value') || 0}`}
                  </span>
                </div>
                <div className="flex justify-between items-center py-1 border-b">
                  <span className="text-muted-foreground">Minimum Amount:</span>
                  <span className="font-medium">
                    ${form.watch('minimum_amount') || '0'}
                  </span>
                </div>
                <div className="flex justify-between items-center py-1 border-b">
                  <span className="text-muted-foreground">
                    Maximum Discount:
                  </span>
                  <span className="font-medium">
                    ${form.watch('maximum_discount') || 'No limit'}
                  </span>
                </div>
                <div className="flex justify-between items-center py-1 border-b">
                  <span className="text-muted-foreground">Usage Limit:</span>
                  <span className="font-medium">
                    {form.watch('usage_limit') || 'Unlimited'}
                  </span>
                </div>
                <div className="flex justify-between items-center py-1 border-b">
                  <span className="text-muted-foreground">User Targeting:</span>
                  <span className="font-medium">
                    {form.watch('user_targeting') === 'all_users' &&
                      'All Users'}
                    {form.watch('user_targeting') === 'first_time_users' &&
                      'First Time Users'}
                    {form.watch('user_targeting') === 'selected_users' &&
                      'Selected Users'}
                  </span>
                </div>
                <div className="flex justify-between items-center py-1 border-b">
                  <span className="text-muted-foreground">
                    User Redemption:
                  </span>
                  <span className="font-medium">
                    {form.watch('user_redemption_limit') === 'once_per_user'
                      ? 'Once Per User'
                      : 'Multiple Per User'}
                  </span>
                </div>
                <div className="flex justify-between items-center py-1 border-b">
                  <span className="text-muted-foreground">
                    Payment Methods:
                  </span>
                  <span className="font-medium">
                    {form.watch('payment_method_restriction') === 'all'
                      ? 'All Methods'
                      : 'Selected Methods'}
                  </span>
                </div>
                <div className="flex justify-between items-center py-1 border-b">
                  <span className="text-muted-foreground">Applies To:</span>
                  <span className="font-medium">
                    {form.watch('applies_to') === 'all' && 'All Products'}
                    {form.watch('applies_to') === '1' && 'Digital Files'}
                    {form.watch('applies_to') === '2' && 'Subscription Package'}
                  </span>
                </div>
                <div className="flex justify-between items-center py-1 border-b">
                  <span className="text-muted-foreground">Valid Period:</span>
                  <span className="font-medium">
                    {form.watch('valid_from') && form.watch('valid_until')
                      ? `${format(
                          new Date(form.watch('valid_from')),
                          'MMM dd, yyyy',
                        )} - ${format(
                          new Date(form.watch('valid_until')),
                          'MMM dd, yyyy',
                        )}`
                      : '-'}
                  </span>
                </div>
                <div className="flex justify-between items-center py-1">
                  <span className="text-muted-foreground">Status:</span>
                  <Badge
                    variant={form.watch('is_active') ? 'default' : 'secondary'}
                  >
                    {form.watch('is_active') ? 'Active' : 'Inactive'}
                  </Badge>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Quick Templates */}
          <Card className="mt-6">
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Zap className="h-5 w-5" />
                Quick Templates
              </CardTitle>
              <CardDescription>
                Apply common coupon configurations
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <Button
                variant="outline"
                size="sm"
                className="w-full justify-start"
                onClick={() => {
                  form.setValue('type', 'percentage');
                  form.setValue('value', 10);
                  form.setValue('minimum_amount', 50);
                  form.setValue('usage_limit', 100);
                  form.setValue('valid_from', datePresets.today);
                  form.setValue('valid_until', datePresets.nextMonth);
                }}
              >
                <Percent className="h-4 w-4 mr-2" />
                10% Off - $50 Min Order
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="w-full justify-start"
                onClick={() => {
                  form.setValue('type', 'fixed');
                  form.setValue('value', 25);
                  form.setValue('minimum_amount', 100);
                  form.setValue('usage_limit', 50);
                  form.setValue('valid_from', datePresets.today);
                  form.setValue('valid_until', datePresets.threeMonths);
                }}
              >
                <DollarSign className="h-4 w-4 mr-2" />
                $25 Off - $100 Min Order
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="w-full justify-start"
                onClick={() => {
                  form.setValue('type', 'percentage');
                  form.setValue('value', 20);
                  form.setValue('minimum_amount', 75);
                  form.setValue('maximum_discount', 50);
                  form.setValue('usage_limit', 200);
                  form.setValue('valid_from', datePresets.today);
                  form.setValue('valid_until', datePresets.sixMonths);
                }}
              >
                <Percent className="h-4 w-4 mr-2" />
                20% Off - Max $50 Discount
              </Button>
            </CardContent>
          </Card>

          {/* Tips Card */}
          <Card className="mt-6">
            <CardHeader>
              <CardTitle className="text-base">💡 Tips</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm">
              <div className="flex items-start gap-2">
                <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                <span>
                  Use clear, memorable coupon codes that customers can easily
                  remember.
                </span>
              </div>
              <div className="flex items-start gap-2">
                <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                <span>
                  Set reasonable minimum order amounts to encourage larger
                  purchases.
                </span>
              </div>
              <div className="flex items-start gap-2">
                <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                <span>
                  Use usage limits to create urgency and prevent abuse.
                </span>
              </div>
              <div className="flex items-start gap-2">
                <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                <span>
                  Test your coupon before making it live to ensure it works
                  correctly.
                </span>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

const AddEditCouponWithErrorBoundary: React.FC = () => {
  return (
    <ErrorBoundary>
      <AddEditCoupon />
    </ErrorBoundary>
  );
};

export default AddEditCouponWithErrorBoundary;
